import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { TeamContext } from 'contexts/TeamContext';
import { createAPIClient } from 'api/client';

const CalendarStateContext = createContext();

export const CalendarStateProvider = ({ children }) => {
  const [rsvpState, setRsvpState] = useState({});
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [api, setApi] = useState(null);
  const { currentTeamId, loading: teamLoading } = useContext(TeamContext);
  const [organizationHosts, setOrganizationHosts] = useState({});

  // Initialize API client
  useEffect(() => {
    const initAPI = async () => {
      try {
        const apiClient = await createAPIClient();
        setApi(apiClient);
      } catch (error) {
        console.error('Failed to initialize API client:', error);
      }
    };
    
    initAPI();
  }, []);

  // Load organization hosts (admins)
  useEffect(() => {
    const loadOrganizationHosts = async () => {
      if (!api || !currentTeamId) return;

      try {
        const response = await api.organizations.getOrganizationAdmins();
        if (response.error) throw response.error;

        // Group hosts by organization_id
        const hostsMap = response.profiles.reduce((acc, profile) => {
          if (!acc[profile.organization_id]) {
            acc[profile.organization_id] = [];
          }
          acc[profile.organization_id].push({
            id: profile.id,
            name: profile.profiledata?.name || profile.email,
            avatar_url: profile.profiledata?.avatar_url
          });
          return acc;
        }, {});

        setOrganizationHosts(hostsMap);
      } catch (error) {
        console.error('Error loading organization hosts:', error);
      }
    };

    loadOrganizationHosts();
  }, [api, currentTeamId]);

  // Load initial calendar data and RSVPs
  useEffect(() => {
    const loadData = async () => {
      if (teamLoading || !api) {
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Fetch calendar events with host details
        const eventsResponse = await api.calendar.getCalendars();
        const events = Array.isArray(eventsResponse) ? eventsResponse : [];

        // Fetch host details for all events
        const eventsWithHosts = await Promise.all(events.map(async (event) => {
          if (!event.hosts || event.hosts.length === 0) return event;

          try {
            const hostProfiles = await api.profiles.getProfilesByIds(event.hosts);
            return {
              ...event,
              hostDetails: hostProfiles.map(profile => ({
                id: profile.id,
                name: profile.name,
                avatar_url: profile.avatar_url
              }))
            };
          } catch (error) {
            console.error('Error fetching host details:', error);
            return event;
          }
        }));
        
        setEvents(eventsWithHosts);
        
        // Fetch RSVPs
        const rsvpResponse = await api.calendar.getCalendarProfiles();
        const rsvpMap = (Array.isArray(rsvpResponse) ? rsvpResponse : []).reduce((acc, rsvp) => {
          acc[rsvp.calendar_id] = rsvp.rsvp_status;
          return acc;
        }, {});
        
        setRsvpState(rsvpMap);
      } catch (error) {
        console.error('Error loading calendar data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [api, currentTeamId, teamLoading]);

  // Update RSVP status
  const updateRSVP = useCallback(async (calendarId, rsvpStatus) => {
    if (!api || !currentTeamId) {
      console.error('Missing required data:', { api: !!api, currentTeamId });
      return { data: null, error: new Error('Missing required data') };
    }

    const data = {
      calendar_id: String(calendarId),
      team_id: currentTeamId,
      rsvp_status: rsvpStatus === true
    };
    
    try {
      const response = await api.calendar.updateCalendarProfile(data);

      // Optimistically update state
      setRsvpState(prev => ({
        ...prev,
        [calendarId]: rsvpStatus
      }));

      if (response.error) throw response.error;
      return { data: response.data || true, error: null };
    } catch (error) {
      console.error('Error updating RSVP:', error);
      return { data: null, error };
    }
  }, [api, currentTeamId]);

  // Update or create event
  const updateEvent = useCallback(async (eventData) => {
    if (!api) {
      console.error('API client not initialized');
      return { data: null, error: new Error('API client not initialized') };
    }

    try {
      // Prepare event data with new fields
      const preparedData = {
        ...eventData,
        hosts: eventData.hosts || [],
        is_recurring: eventData.is_recurring || false,
        recurrence_pattern: eventData.recurrence_pattern || null,
        meta: eventData.meta || {}
      };

      const response = await api.calendar.updateCalendar(preparedData);
      if (response.error) throw response.error;

      // Fetch host details for the updated event
      let updatedEvent = response.data;
      if (updatedEvent.hosts?.length > 0) {
        const hostProfiles = await api.profiles.getProfilesByIds(updatedEvent.hosts);
        updatedEvent = {
          ...updatedEvent,
          hostDetails: hostProfiles.map(profile => ({
            id: profile.id,
            name: profile.name,
            avatar_url: profile.avatar_url
          }))
        };
      }

      // Update events state
      setEvents(prevEvents => {
        if (eventData._delete) {
          return prevEvents.filter(event => event.id !== eventData.id);
        } else if (eventData.id) {
          return prevEvents.map(event => 
            event.id === eventData.id ? updatedEvent : event
          );
        } else {
          return [...prevEvents, updatedEvent];
        }
      });

      return { data: updatedEvent, error: null };
    } catch (error) {
      console.error('Error updating event:', error);
      return { data: null, error };
    }
  }, [api]);

  const value = React.useMemo(() => ({
    events,
    rsvpState,
    isLoading,
    updateRSVP,
    updateEvent,
    organizationHosts
  }), [events, rsvpState, isLoading, updateRSVP, updateEvent, organizationHosts]);

  return (
    <CalendarStateContext.Provider value={value}>
      {children}
    </CalendarStateContext.Provider>
  );
};

export const useCalendarState = () => {
  const context = useContext(CalendarStateContext);
  if (!context) {
    throw new Error('useCalendarState must be used within a CalendarStateProvider');
  }
  return context;
}; 